'use client'

import {isValidElement, type ReactNode, type RefObject, useEffect, useRef, useState} from 'react'
import {usePathname, useRouter, useSearchParams, useSelectedLayoutSegments} from 'next/navigation'
import {resolveDefaultModalBackground, resolveRoutedModal, type ModalApp} from './core'

const MAX_RECOVERY_AGE_MS = 15_000

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

const isFocusable = (element: HTMLElement) => {
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false
    if (element.closest('[inert]')) return false
    return element.getClientRects().length > 0
}

export const useDialogFocusTrap = (
    enabled: boolean,
    containerRef: RefObject<HTMLElement | null>,
    {tabNavigation = 'native'}: {tabNavigation?: 'native' | 'all-controls'} = {}
) => {
    useEffect(() => {
        if (!enabled) return

        const handleTab = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || event.defaultPrevented) return
            const container = containerRef.current
            if (!container) return

            const modalRoot = container.closest<HTMLElement>('[data-modal-root]')
            if (modalRoot && !isTopmostModal(modalRoot)) return

            const focusable = Array.from(
                container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            ).filter(isFocusable)
            if (focusable.length === 0) {
                event.preventDefault()
                container.focus()
                return
            }

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement as HTMLElement | null
            if (!active || !focusable.includes(active)) {
                event.preventDefault()
                ;(event.shiftKey ? last : first).focus()
                return
            }
            if (tabNavigation === 'all-controls') {
                // Selection dialogs include option buttons in the Tab order even
                // when Safari's system setting skips buttons during native Tab.
                event.preventDefault()
                const direction = event.shiftKey ? -1 : 1
                const nextIndex = (focusable.indexOf(active) + direction + focusable.length) % focusable.length
                focusable[nextIndex].focus()
            } else if (event.shiftKey && (active === first || !container.contains(active))) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && (active === last || !container.contains(active))) {
                event.preventDefault()
                first.focus()
            }
        }

        const capture = tabNavigation === 'all-controls'
        document.addEventListener('keydown', handleTab, capture)
        return () => document.removeEventListener('keydown', handleTab, capture)
    }, [containerRef, enabled, tabNavigation])
}

export const MODAL_ESCAPE_HANDLED = Symbol('modalEscapeHandled')

export type ModalKeyboardEvent = KeyboardEvent & {
    [MODAL_ESCAPE_HANDLED]?: boolean
}

const isVisibleModalRoot = (element: HTMLElement) => {
    if (!element.isConnected || element.hidden || element.closest('[hidden], [inert], [aria-hidden="true"]')) {
        return false
    }

    const styles = window.getComputedStyle(element)
    const layoutAvailable = document.documentElement.getClientRects().length > 0
    return styles.display !== 'none'
        && styles.visibility !== 'hidden'
        && styles.visibility !== 'collapse'
        && (!layoutAvailable || element.getClientRects().length > 0)
}

export const isTopmostModal = (root: HTMLElement | null) => {
    if (!root || !isVisibleModalRoot(root)) return false
    const openDialogs = Array.from(document.querySelectorAll<HTMLElement>('[data-modal-root]'))
        .filter(isVisibleModalRoot)
    const zIndex = (element: HTMLElement) => {
        const value = element.style.zIndex || window.getComputedStyle(element).zIndex
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : 0
    }
    const topmost = openDialogs.reduce<HTMLElement | null>((current, candidate) => {
        if (!current || zIndex(candidate) >= zIndex(current)) return candidate
        return current
    }, null)
    return topmost === root
}

export const markModalEscapeHandled = (event: KeyboardEvent) => {
    const modalEvent = event as ModalKeyboardEvent
    if (modalEvent[MODAL_ESCAPE_HANDLED]) return false
    modalEvent[MODAL_ESCAPE_HANDLED] = true
    return true
}

/**
 * Handles Escape after target-level controls have had a chance to consume it.
 * This keeps nested selects and utility dialogs independent while ensuring that
 * a routed modal still closes when focus is inside a control that stops bubbling.
 */
export const useTopmostModalEscape = (
    enabled: boolean,
    rootRef: RefObject<HTMLElement | null>,
    onEscape: () => void
) => {
    const onEscapeRef = useRef(onEscape)

    useEffect(() => {
        onEscapeRef.current = onEscape
    }, [onEscape])

    useEffect(() => {
        if (!enabled) return

        const pending = new Set<number>()
        const handleEscapeCapture = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            // Native events can flush microtasks between capture and target
            // listeners. A new task lets controls consume the complete event.
            const timer = window.setTimeout(() => {
                pending.delete(timer)
                const modalEvent = event as ModalKeyboardEvent
                if (event.defaultPrevented || modalEvent[MODAL_ESCAPE_HANDLED]) return
                if (!isTopmostModal(rootRef.current) || !markModalEscapeHandled(event)) return

                event.preventDefault()
                onEscapeRef.current()
            }, 0)
            pending.add(timer)
        }

        window.addEventListener('keydown', handleEscapeCapture, true)
        return () => {
            window.removeEventListener('keydown', handleEscapeCapture, true)
            pending.forEach(timer => window.clearTimeout(timer))
        }
    }, [enabled, rootRef])
}

const PROFILES = {
    partner: {
        basePath: '/admin',
        fallback: '/dashboard',
        recoverableBackground: /^\/(?:calendar|clients|dashboard|more|payments|templates)(?:\/|$)/,
        storageKey: 'fitro:partner-modal-deep-link',
    },
    customer: {
        basePath: '',
        fallback: '/home',
        recoverableBackground: /^\/(?:calendar|home|my-classes|payments|profile|studio)(?:\/|$)/,
        storageKey: 'fitro:customer-modal-deep-link',
    },
} as const

interface PendingModalNavigation {
    backgroundPath: string
    targetPath: string
    createdAt: number
    stage: 'background' | 'reopening'
    entryKey?: string
}

export function ModalSlotEmpty() {
    return null
}

ModalSlotEmpty.displayName = 'ModalSlotEmpty'

const isEmptyModalSlot = (modal: ReactNode): boolean => {
    if (modal === null || modal === undefined || typeof modal === 'boolean') return true
    if (Array.isArray(modal)) return modal.every(isEmptyModalSlot)
    if (!isValidElement(modal)) return false
    if (modal.type === ModalSlotEmpty) return true

    const props = modal.props as {children?: ReactNode}
    if (!Object.prototype.hasOwnProperty.call(props, 'children')) return false
    return isEmptyModalSlot(props.children)
}

const stripBasePath = (path: string, basePath: string) => {
    let normalized = path || '/'
    while (basePath && (normalized === basePath || normalized.startsWith(`${basePath}/`))) {
        normalized = normalized.slice(basePath.length) || '/'
    }
    return normalized
}

const routePathname = (value: string) => {
    try {
        return new URL(value, 'http://localhost').pathname.replace(/\/+$/, '') || '/'
    } catch {
        return value.split('?')[0].replace(/\/+$/, '') || '/'
    }
}

const comparablePath = (value: string) => {
    try {
        const url = new URL(value, 'http://localhost')
        return `${url.pathname.replace(/\/+$/, '') || '/'}${url.search}`
    } catch {
        return value.split('#')[0]
    }
}

const matchesBackground = (expected: string, active: string) => {
    try {
        const expectedUrl = new URL(expected, 'http://localhost')
        const activeUrl = new URL(active, 'http://localhost')
        if (routePathname(expected) !== routePathname(active)) return false

        const activeParams = new Map<string, number>()
        for (const entry of activeUrl.searchParams) {
            const key = JSON.stringify(entry)
            activeParams.set(key, (activeParams.get(key) ?? 0) + 1)
        }
        for (const entry of expectedUrl.searchParams) {
            const key = JSON.stringify(entry)
            const count = activeParams.get(key) ?? 0
            if (count === 0) return false
            activeParams.set(key, count - 1)
        }
        return true
    } catch {
        return comparablePath(expected) === comparablePath(active)
    }
}

const readPending = (storageKey: string): PendingModalNavigation | null | undefined => {
    try {
        const value = window.sessionStorage.getItem(storageKey)
        if (!value) return null
        const pending = JSON.parse(value) as Partial<PendingModalNavigation>
        if (typeof pending.backgroundPath !== 'string'
            || typeof pending.targetPath !== 'string'
            || typeof pending.createdAt !== 'number'
            || (pending.stage !== 'background' && pending.stage !== 'reopening')
            || Date.now() - pending.createdAt > MAX_RECOVERY_AGE_MS) {
            window.sessionStorage.removeItem(storageKey)
            return null
        }
        return pending as PendingModalNavigation
    } catch {
        return undefined
    }
}

const writePending = (storageKey: string, pending: PendingModalNavigation) => {
    try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(pending))
        return true
    } catch {
        return false
    }
}

/**
 * Opens a routed modal over its background, for a link from outside the app (the studio's public
 * page): the background reopens the modal on arrival, as after a reload, so the modal never shows
 * first as a full page. `navigate` goes to the background (a document load by default; a router
 * transition works as well). Returns false when the visit cannot be remembered.
 */
export function openRoutedModalOverBackground(
    app: ModalApp,
    backgroundPath: string,
    targetPath: string,
    navigate: (url: string) => void = (url) => window.location.assign(url),
): boolean {
    const profile = PROFILES[app]
    if (!writePending(profile.storageKey, {backgroundPath, targetPath, createdAt: Date.now(), stage: 'background'})) return false
    navigate(`${profile.basePath}${backgroundPath}`)
    return true
}

export function useFitroModalRouting({
    app,
    modal,
    isDirectModalPage,
    isFullscreenViewport,
    isHydrated,
    resolveSafeReturnTo,
    resolveRecoveryBackground,
    recoveryNavigation = 'document',
    isRecoveryReady = true,
    notificationSettingsPath,
    historyEntry,
}: {
    app: ModalApp
    modal?: ReactNode
    isDirectModalPage: boolean
    isFullscreenViewport: boolean
    isHydrated: boolean
    resolveSafeReturnTo: (value?: string | null, fallback?: string) => string
    /** The page a reloaded or re-entered modal was last opened over, when the app remembers it. */
    resolveRecoveryBackground?: (path: string) => string | null
    recoveryNavigation?: 'document' | 'spa'
    isRecoveryReady?: boolean
    notificationSettingsPath?: string
    /** Preserve the reloaded visit while rebuilding its routed-modal background. */
    historyEntry?: {capture: () => string; restore: (entryKey: string, targetUrl: string) => void}
}) {
    const profile = PROFILES[app]
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const modalSegments = useSelectedLayoutSegments('modal') ?? []
    const pageSegments = useSelectedLayoutSegments() ?? []
    const router = useRouter()
    const currentPath = stripBasePath(pathname ?? '/', profile.basePath)
    const initialDirectPath = useRef(isDirectModalPage ? routePathname(currentPath) : null)
    const [initialDirectRouteActive, setInitialDirectRouteActive] = useState(isDirectModalPage)
    const isInitialDirectPath = initialDirectPath.current === routePathname(currentPath)
    useEffect(() => {
        if (!isDirectModalPage || !isInitialDirectPath) setInitialDirectRouteActive(false)
    }, [isDirectModalPage, isInitialDirectPath])
    const isInitialDirectModalPage = initialDirectRouteActive
        && isDirectModalPage
        && isInitialDirectPath
    // Deep-link recovery is an entry-time decision. Widening an already usable
    // compact editor must not navigate away and discard its in-memory draft.
    const [initialFullscreenViewport, setInitialFullscreenViewport] = useState<boolean | null>(
        isHydrated ? isFullscreenViewport : null
    )
    useEffect(() => {
        if (isHydrated && initialFullscreenViewport === null) {
            setInitialFullscreenViewport(isFullscreenViewport)
        }
    }, [initialFullscreenViewport, isFullscreenViewport, isHydrated])
    const preserveDirectPage = isInitialDirectModalPage && initialFullscreenViewport === true
    const recoveryStarted = useRef(false)
    const [recoveryFailed, setRecoveryFailed] = useState(false)
    const routeState = resolveRoutedModal({
        currentPath,
        modalSegments,
        pageSegments,
        hasModalContent: Boolean(modal) && !isEmptyModalSlot(modal),
        isDirectModalPage: isInitialDirectModalPage,
        isHydrated,
        isFullscreenViewport: isFullscreenViewport || preserveDirectPage,
        recoveryFailed,
    })
    const search = searchParams?.toString() ?? ''

    useEffect(() => {
        if (!isRecoveryReady || !isHydrated || isFullscreenViewport || preserveDirectPage || !pathname) return

        const activePath = search ? `${currentPath}?${search}` : currentPath
        const pending = readPending(profile.storageKey)
        if (pending === undefined) {
            setRecoveryFailed(true)
            return
        }
        if (pending?.stage === 'reopening' && comparablePath(pending.targetPath) === comparablePath(activePath)) {
            window.sessionStorage.removeItem(profile.storageKey)
            return
        }
        if (pending?.stage === 'reopening' && matchesBackground(pending.backgroundPath, activePath)) {
            if (typeof pending.entryKey === 'string') historyEntry?.restore(pending.entryKey, `${profile.basePath}${pending.targetPath}`)
            router.push(pending.targetPath, {scroll: false})
            return
        }
        if (pending?.stage === 'background'
            && matchesBackground(pending.backgroundPath, activePath)) {
            writePending(profile.storageKey, {...pending, stage: 'reopening'})
            if (typeof pending.entryKey === 'string') historyEntry?.restore(pending.entryKey, `${profile.basePath}${pending.targetPath}`)
            router.push(pending.targetPath, {scroll: false})
            return
        }
        if (!isInitialDirectModalPage
            || routeState.hasRoutedModal
            || recoveryStarted.current) return

        const safeFallback = resolveSafeReturnTo(
            resolveDefaultModalBackground(app, currentPath, notificationSettingsPath),
            profile.fallback
        )
        const requested = resolveSafeReturnTo(
            resolveRecoveryBackground?.(currentPath) ?? searchParams?.get('returnTo'),
            safeFallback
        )
        const requestedBackground = profile.recoverableBackground.test(routePathname(requested))
            ? requested
            : safeFallback
        const backgroundPath = routePathname(requestedBackground) === routePathname(activePath)
            ? safeFallback
            : requestedBackground
        if (comparablePath(backgroundPath) === comparablePath(activePath)) return

        recoveryStarted.current = true
        if (!writePending(profile.storageKey, {
            backgroundPath,
            targetPath: activePath,
            createdAt: Date.now(),
            stage: 'background',
            entryKey: historyEntry?.capture(),
        })) {
            setRecoveryFailed(true)
            return
        }
        const resolvedBackgroundPath = `${profile.basePath}${backgroundPath}`
        if (recoveryNavigation === 'spa') {
            router.replace(resolvedBackgroundPath, {scroll: false})
        } else {
            window.location.replace(resolvedBackgroundPath)
        }
    }, [
        app,
        currentPath,
        isInitialDirectModalPage,
        isFullscreenViewport,
        isHydrated,
        isRecoveryReady,
        pathname,
        preserveDirectPage,
        profile,
        resolveSafeReturnTo,
        resolveRecoveryBackground,
        recoveryNavigation,
        notificationSettingsPath,
        historyEntry,
        routeState.hasRoutedModal,
        router,
        search,
        searchParams,
    ])

    return {...routeState, currentPath}
}
